import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AuthSignalService, User } from '../state/auth.signal';
import { Router } from '@angular/router';
import { tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { PushNotificationService } from '../services/push-notification.service';
import { SocketService } from '../socket/socket.service';
import { CallService } from '../services/call.service';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = `${environment.apiUrl}/auth`;

  constructor(
    private http: HttpClient,
    private authSignal: AuthSignalService,
    private router: Router,
    private pushService: PushNotificationService,
    private socketService: SocketService,
    private callService: CallService
  ) { }

  login(credentials: any) {
    return this.http.post<{ user: User }>(
      `${this.apiUrl}/login`,
      credentials,
      { withCredentials: true }   // needed for the Set-Cookie header
    ).pipe(
      tap(response => {
        // No token in localStorage — the browser stores the HttpOnly cookie
        this.authSignal.setUser(response.user);
        // Conectar e identificar el socket para presencia en tiempo real
        this.socketService.identify(response.user.userId);
        // Solo tras autenticarse: `iniciar()` pide el permiso de micrófono, y
        // hacerlo en la pantalla de login sería a la vez inútil y desconcertante.
        this.callService.iniciar();
        this.pushService.init();
      })
    );
  }

  /** Login con Google: envía el access token de Google Identity Services al backend.
   *  El backend lo verifica, valida que el usuario exista y esté activo, y devuelve
   *  la misma cookie HttpOnly que el login normal (misma persistencia de sesión). */
  loginWithGoogle(accessToken: string) {
    return this.http.post<{ user: User }>(
      `${this.apiUrl}/google`,
      { accessToken },
      { withCredentials: true }   // needed for the Set-Cookie header
    ).pipe(
      tap(response => {
        this.authSignal.setUser(response.user);
        this.socketService.identify(response.user.userId);
        this.callService.iniciar();
        this.pushService.init();
      })
    );
  }

  /** Restores user session from the server-side cookie. Called by AuthGuard on page refresh. */
  loadCurrentUser() {
    return this.http.get<User>(`${this.apiUrl}/me`, { withCredentials: true }).pipe(
      tap(user => {
        this.authSignal.setUser(user);
        // Re-identificar el socket al restaurar la sesión (ej: recarga de página F5)
        this.socketService.identify(user.userId);
        this.callService.iniciar();
      })
    );
  }

  logout() {
    // Desconectar socket ANTES de limpiar el estado — esto notifica al servidor
    // que el usuario ya no está en línea y evita que sigan llegando eventos WS
    this.socketService.disconnect();
    this.http.post(`${this.apiUrl}/logout`, {}, { withCredentials: true }).subscribe();
    this.pushService.logout();
    this.authSignal.logout();
    this.router.navigate(['/login']);
  }
}
